// While a deploy restarts the server, nginx answers 502 and a service behind it 503 for some seconds: a read is asked
// again until it answers, so a person sees a slower page instead of an error. A write is never repeated - it may
// have reached the server.

const RESTARTING = new Set([502, 503, 504]);
// A slow job's 504 means the server is still working on it: asking again would start the job twice.
const SLOW_JOB_RESTARTING = new Set([502, 503]);
const READS = new Set(['get', 'head', 'options']);
export const RESTART_PATIENCE_MS = 60000;
const FIRST_WAIT_MS = 1000;
const LONGEST_WAIT_MS = 8000;

const pause = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export function waitOutRestarts(client, { sleep = pause, now = Date.now } = {}) {
  client.interceptors.response.use(undefined, async (error) => {
    const config = error.config;
    if (!config || !READS.has((config.method || 'get').toLowerCase())) {
      throw error;
    }
    const restartStatuses = config.slowJob ? SLOW_JOB_RESTARTING : RESTARTING;
    // A slow job's lost connection may have reached the server, which goes on working: only its own answer is sure.
    const restarting = error.response
      ? restartStatuses.has(error.response.status)
      : !config.slowJob && error.code === 'ERR_NETWORK';
    if (!restarting) {
      throw error;
    }
    const retry = config.restartRetry || { since: now(), attempt: 0 };
    const wait = Math.min(FIRST_WAIT_MS * 2 ** retry.attempt, LONGEST_WAIT_MS);
    if (now() - retry.since + wait > RESTART_PATIENCE_MS) {
      throw error;
    }
    config.restartRetry = { ...retry, attempt: retry.attempt + 1 };
    await sleep(wait);
    return client(config);
  });
}
